import * as React from 'react';
import classnames from 'classnames';

import OCButton from '~/components/button';
import Checkbox from '~/components/ui/Checkbox';
import TextInput from '~/components/ui/TextInput';
import useMedia from '~/hooks/use-media';
import { ModalView } from '~/providers/modal-context';
import { useServicesContext } from '~/providers/services-context';
import { type StateCode } from '~/services/actions/state-actions';
import { promisifiedDelay } from '~/utils/promisifiedDelay';

import onboardingActions from './store/actions';
import onboardingReducer from './store/reducers';
import styles from './Onboarding.module.css';

export const onboardingVariants = {
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  USER_SIGNUP: 'user_signup',
  ODDSCHECKER_PLUS_AD: 'oddschecker_plus_ad'
};

export type OnboardingVariant =
(typeof onboardingVariants)[keyof typeof onboardingVariants];

export const onboardingView = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary'
};

export type OnboardingView =
(typeof onboardingView)[keyof typeof onboardingView];

export type OnboardingProps = {
  baseUrl?: string;
  contentServer?: string;
  hasMarginTop?: boolean;
  initLabels?: Record<string, any>;
  state: StateCode;
  variant?: OnboardingVariant;
  view?: OnboardingView;
};

export const Onboarding = ({
  baseUrl = '/',
  contentServer = '',
  hasMarginTop,
  state,
  variant = onboardingVariants.NEWSLETTER_SIGNUP,
  view = onboardingView.SECONDARY
}: OnboardingProps) => {
  const { labelActions } = useServicesContext();
  const { isDesktop, isMobile } = useMedia();

  const shouldRenderAsSecondary = isMobile || view === onboardingView.SECONDARY;
  const shouldRenderAsMyOCLoginCTA =
  view === onboardingView.SECONDARY &&
  variant === onboardingVariants.USER_SIGNUP;

  const isHandicapperOrPicksPage = false;

  const mobileAndSecondary = !isDesktop || view === onboardingView.SECONDARY;
  const desktopAndPrimary = isDesktop && view !== onboardingView.SECONDARY;

  // #1 State

  const [{ agreeTerms, errorOnBlur, input, submitted }, dispatch] =
  React.useReducer(onboardingReducer, {
    agreeTerms: false,
    errorOnBlur: false,
    input: { isValid: false, value: '' },
    submitted: false
  });

  const inputIsValid = input.isValid;
  const canSubmit = shouldRenderAsMyOCLoginCTA ?
  inputIsValid :
  inputIsValid && agreeTerms;

  // #2 Helpers

  const { handleFormSubmit, handleInputBlur, handleInputChange, toggleTerms } =
  onboardingActions(dispatch);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleInputChange(event.target.value);
  };

  const handleBlur = (): void => {
    handleInputBlur();
  };

  const updateMyOcEmailInputWithReties = async (i = 0) => {
    if (i > 3) return;

    const myocLogin = document.querySelector("div[id='myoc-login-modal']");
    const emailInput = myocLogin?.querySelector('input[type=email]');

    if (emailInput) {
      (emailInput as HTMLInputElement).value = input.value;
      return;
    }

    await promisifiedDelay(500);
    updateMyOcEmailInputWithReties(i += 1);
  };

  const handleSubmit = async (): Promise<void> => {
    if (shouldRenderAsMyOCLoginCTA) {
      try {
        window.openModalAtView(ModalView.SignupForm);

        await promisifiedDelay(500);

        updateMyOcEmailInputWithReties();
      } catch (err) {
        console.log('error at handleSubmit');
        console.log(err);
      }
      return;
    }

    handleFormSubmit(input.value, state, baseUrl);
  };

  // #3 Renderers

  const renderBgImg = (): JSX.Element => {
    const backgroundImage = shouldRenderAsSecondary ?
    labelActions.get('ONBOARDING_SECONDARY_BG_IMG') :
    labelActions.get('ONBOARDING_PRIMARY_BG_IMG');
    const backgroundImageUrl = `${contentServer}${backgroundImage}`;

    return (
      <img
        alt="onboarding-background"
        className={styles.bgImg}
        src={backgroundImageUrl} />);


  };

  const renderTitle = (): JSX.Element => {
    return (
      <div className={styles.onboardingTitle}>
				<h2 className={isHandicapperOrPicksPage ? styles.titleSmall : ''}>
					{labelActions.get('SIGN_UP_WITH_ODDSCHECKER')}
				</h2>
				<h2
          className={classnames(
            styles.highlight,
            isHandicapperOrPicksPage ? styles.small : ''
          )}>

					{labelActions.get('TO_BEAT_THE_ODDS')}
				</h2>
			</div>);

  };

  const renderDescription = (): JSX.Element => {
    return (
      <div className={styles.description}>
				{labelActions.get('SUBSCRIBE_TO_EMAIL')}
			</div>);

  };

  const renderTextInput = (): JSX.Element => {
    return (
      <TextInput
        data-testid="onboarding-email-input"
        hasError={errorOnBlur}
        inputClassName={classnames(
          styles.input,
          errorOnBlur ? styles.error : ''
        )}
        isValid={inputIsValid}
        name="email"
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={labelActions.get('YOUR_EMAIL_ADDRESS')}
        shouldAutocomplete={false}
        type="email"
        value={input.value}
        wrapperClassName={styles.inputContainer} />);


  };

  const renderErrorBanner = (): JSX.Element | null => {
    if (!errorOnBlur) return null;

    return (
      <div className={styles.errorBanner} data-testid="error-banner">
				<div className={styles.errorTitle}>
					{labelActions.get('INVALID_EMAIL')}
				</div>
				<div className={styles.errorMessage}>
					{labelActions.get('EMAIL_ADDRESS_FORMAT')}
				</div>
			</div>);

  };

  const renderCTA = (): JSX.Element => {
    const ctaText =
    shouldRenderAsMyOCLoginCTA || !submitted ?
    labelActions.get('SIGN_ME_UP') :
    labelActions.get('SIGNED_UP');

    return (
      <div className={styles.ctaWrapper}>
				<OCButton
          className={styles.ctaBtn}
          disabled={!canSubmit}
          onClick={handleSubmit}
          testId="onboarding-submit"
          type="secondary">

					{ctaText}
				</OCButton>
			</div>);

  };

  const renderLegalCheckbox = (): JSX.Element | null => {
    if (shouldRenderAsMyOCLoginCTA) return null;

    const termsAndPrivacyLabel = labelActions.
    get('YES_TO_TERMS').
    replace(/%1/gi, labelActions.get('TERMS_URL')).
    replace(/%2/gi, labelActions.get('PRIVACY_URL'));

    return (
      <Checkbox
        checked={agreeTerms}
        className={styles.terms}
        name="terms"
        onClick={toggleTerms}
        testId="onboarding-terms">

				<span
          className={styles.terms}
          dangerouslySetInnerHTML={{
            __html: termsAndPrivacyLabel
          }} />

			</Checkbox>);

  };

  return (
    <div
      className={classnames(
        styles.wrapper,
        shouldRenderAsSecondary ? styles.secondary : styles.primary,
        shouldRenderAsMyOCLoginCTA ? styles.isSignupCta : '',
        hasMarginTop ? styles.marginTop : ''
      )}
      data-testid="onboarding">

			{renderBgImg()}
			<div className={styles.bgColor} />
			<div className={styles.contentWrapper}>
				{renderTitle()}
				<div className={styles.formContainer}>
					{renderDescription()}
					<div className={styles.inputWrapper}>
						{renderTextInput()}
						{mobileAndSecondary ? renderErrorBanner() : null}
						{renderCTA()}
					</div>
					{desktopAndPrimary ? renderErrorBanner() : null}
					{renderLegalCheckbox()}
				</div>
			</div>
		</div>);

};